import { useState, useRef, useEffect } from 'react';
import Note from './Note';
import AddIcon from '@mui/icons-material/Add';
import NotesList from './NotesList';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { authedApiFetch } from '../../utils/Api';

/**
 * NotesInterface Component
 *
 * A component that provides a notes interface for creating and managing notes
 *
 * @param {string} tenantId - ID of the current tenant
 * @returns {JSX.Element} The NotesInterface component
 */
const NotesInterface = ({
	tenantId
}) => {
	// View mode for editing, viewing, or listing notes
	const [viewMode, setViewMode] = useState('edit'); // 'edit', 'view', or 'list'
	const [currentNote, setCurrentNote] = useState(null);
	const [selectedNoteId, setSelectedNoteId] = useState(null);

	const [recentNotes, setRecentNotes] = useState([]);
	const [isLoadingNotes, setIsLoadingNotes] = useState(false);

	// Fetch notes from the backend API
	const fetchNotes = async () => {
		setIsLoadingNotes(true);
		try {
			const response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/notes`,
				method: 'GET',
			});
			setRecentNotes(response);
		} catch (error) {
			console.error('Error fetching notes:', error);
		} finally {
			setIsLoadingNotes(false);
		}
	};

	// Load notes on initial render or when mode changes to notes
	useEffect(() => {
		if (viewMode === 'list') {
			fetchNotes();
		}
	}, [viewMode]);

	const textAreaRef = useRef(null);

	/**
	 * Handle creating a new note
	 * Resets the form and focuses the text area
	 * Also switches from view mode to edit mode if applicable
	 */
	const handleNewNote = () => {
		// Reset form
		setViewMode('edit');
		setCurrentNote(null);
		setSelectedNoteId(null);
		
		// Focus the textarea after clearing everything
		setTimeout(() => {
			if (textAreaRef.current) {
				textAreaRef.current.focus();
			}
		}, 0);
	};
	
	/**
	 * Show the list of past notes
	 */
	const handleShowPastNotes = () => {
		setViewMode('list');
		setCurrentNote(null);
		setSelectedNoteId(null);
	};
	
	/**
	 * Handle selecting a note from the past notes list
	 * @param {Object} note - The selected note
	 */
	const handleSelectNote = (note) => {
		setCurrentNote(note);
		setSelectedNoteId(note.id);
		setViewMode('view');
	};
	
	return (
		<>
			{/* Show the edit form or the saved note view based on viewMode */}
			{viewMode === 'edit' && (
				<Note
					note={currentNote}
					tenantId={tenantId}
					onShowPastNotes={handleShowPastNotes}
					onNewNote={handleNewNote}
					readOnly={false}
				/>
			)}
			{viewMode === 'view' && (
				<Note
					note={currentNote}
					tenantId={tenantId}
					onShowPastNotes={handleShowPastNotes}
					onNewNote={handleNewNote}
					readOnly={true}
				/>
			)}
			{viewMode === 'list' && (
				<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
					<Typography variant='h6' component='h2'>
						Notes
					</Typography>
					<Button
						variant='contained'
						color='primary'
						startIcon={<AddIcon />}
						fullWidth
						onClick={handleNewNote}
						size='small'
					>
						New Note
					</Button>
					<NotesList 
						notes={recentNotes}
						onSelectNote={handleSelectNote}
						selectedNoteId={selectedNoteId}
						loading={isLoadingNotes}
					/>
				</Box>
			)}
		</>
	);
};

export default NotesInterface;
